import { publicHref } from '../lib/publicUrls';
import './EditorialPages.css';
import { useParams, Link } from 'react-router-dom';
import { Children, isValidElement, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { legacyBlogPosts } from '../data/legacyBlogPosts';
import PhotoSlot, { photoKey } from '../components/PhotoSlots';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, User, Calendar, ChevronDown, Quote, Lightbulb, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { TourClosing } from '../components/TourJourney';
import ReadingProgressBar from '../components/ReadingProgressBar';
import TableOfContents from '../components/TableOfContents';
import ArticleCTABox from '../components/ArticleCTABox';

// Slugify heading text → id (must match TableOfContents.slugify)
const headingId = (children) => {
  const plainText = nodes => Children.toArray(nodes).map(node => isValidElement(node) ? plainText(node.props.children) : String(node)).join('');
  const text = plainText(children).toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 64);
  return text;
};

// Detect special blockquote markers for stylized callout boxes
const detectCallout = (text) => {
  const t = (text || '').trim();
  if (/^(💡|tip:|tip —)/i.test(t)) return 'tip';
  if (/^(⚠️|warning:|note:|important:)/i.test(t)) return 'warning';
  if (/^(✓|✅|key takeaway:|takeaway:)/i.test(t)) return 'takeaway';
  return null;
};

const calloutStyles = {
  tip:       { Icon: Lightbulb,      bg: 'bg-amber-50',  border: 'border-amber-400',   iconColor: 'text-amber-600',  label: 'TIP' },
  warning:   { Icon: AlertTriangle,  bg: 'bg-red-50',    border: 'border-red-400',     iconColor: 'text-red-600',    label: 'IMPORTANT' },
  takeaway:  { Icon: CheckCircle2,   bg: 'bg-green-50',  border: 'border-green-500',   iconColor: 'text-green-600',  label: 'KEY TAKEAWAY' },
};

// react-markdown components — premium typography + interactive elements
export const makeMarkdownComponents = () => ({
  a: ({ href, children, ...props }) => {
    if (href && href.startsWith('/')) {
      return (
        <Link
          to={publicHref(href)}
          className="text-[#0072BB] underline decoration-[#0072BB]/30 underline-offset-4 hover:decoration-[#0072BB] hover:text-[#005a94] font-medium transition-colors"
          onClick={() => window.scrollTo(0, 0)}
        >
          {children}
        </Link>
      );
    }
    return (
      <a
        href={publicHref(href)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0072BB] underline decoration-[#0072BB]/30 underline-offset-4 hover:decoration-[#0072BB] hover:text-[#005a94] font-medium transition-colors"
        {...props}
      >
        {children}
      </a>
    );
  },
  h2: ({ children }) => (
    <h2 id={headingId(children)} className="dv-size-heading scroll-mt-24 text-3xl md:text-4xl font-bold mt-14 mb-5 text-gray-900 tracking-tight relative pl-4 border-l-[6px] border-[#0072BB]">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 id={headingId(children)} className="dv-size-subheading scroll-mt-24 text-2xl md:text-3xl font-bold mt-10 mb-4 text-gray-900 tracking-tight">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="dv-size-subheading text-xl font-semibold mt-7 mb-3 text-gray-900">{children}</h4>
  ),
  p: ({ children }) => (
    <p className="dv-size-body text-gray-800 leading-[1.85] mb-6 text-lg">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-none ml-0 mb-6 space-y-3 text-gray-800 text-lg [&>li]:relative [&>li]:pl-7 [&>li]:before:content-[''] [&>li]:before:absolute [&>li]:before:left-0 [&>li]:before:top-[0.7em] [&>li]:before:h-2 [&>li]:before:w-2 [&>li]:before:rounded-full [&>li]:before:bg-[#0072BB]">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside ml-6 mb-6 space-y-3 text-gray-800 text-lg marker:text-[#0072BB] marker:font-bold">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="dv-size-body leading-[1.8]">{children}</li>,
  blockquote: ({ children }) => {
    // Pull plain text out of children to test callout markers
    const flat = (() => {
      try {
        return (Array.isArray(children) ? children : [children])
          .map((c) => (typeof c === 'string' ? c : (c?.props?.children || '')))
          .flat(Infinity)
          .map((c) => (typeof c === 'string' ? c : ''))
          .join('')
          .trim();
      } catch (_e) {
        return '';
      }
    })();
    const variant = detectCallout(flat);
    if (variant) {
      const { Icon, bg, border, iconColor, label } = calloutStyles[variant];
      // Strip the marker prefix from the rendered text
      const cleaned = flat.replace(/^(💡|⚠️|✓|✅|tip[:\s—-]+|note[:\s]+|warning[:\s]+|important[:\s]+|key takeaway[:\s]+|takeaway[:\s]+)/i, '');
      return (
        <aside className={`my-8 rounded-r-xl border-l-4 ${border} ${bg} p-5 flex gap-4`} data-testid={`callout-${variant}`}>
          <Icon className={`shrink-0 mt-1 ${iconColor}`} size={22} />
          <div className="flex-1">
            <div className={`text-xs font-bold tracking-widest mb-1 ${iconColor}`}>{label}</div>
            <p className="dv-size-body text-gray-800 leading-relaxed mb-0">{cleaned}</p>
          </div>
        </aside>
      );
    }
    // Default: dramatic pull-quote
    return (
      <blockquote className="my-10 relative pl-12 pr-4 py-2 border-l-[6px] border-[#0072BB] bg-gradient-to-r from-blue-50/60 to-transparent rounded-r-lg">
        <Quote className="absolute -left-1 top-2 text-[#0072BB]/30" size={36} />
        <div className="dv-size-body text-xl md:text-2xl text-gray-800 font-light leading-relaxed">{children}</div>
      </blockquote>
    );
  },
  strong: ({ children }) => <strong className="font-bold text-gray-900">{children}</strong>,
  em: ({ children }) => <em className="not-italic text-gray-700">{children}</em>,
  hr: () => (
    <div className="my-12 flex items-center justify-center gap-2" aria-hidden="true">
      <span className="h-1 w-1 rounded-full bg-[#0072BB]"></span>
      <span className="h-1 w-1 rounded-full bg-[#0072BB]"></span>
      <span className="h-1 w-1 rounded-full bg-[#0072BB]"></span>
    </div>
  ),
  img: ({ src, alt }) => (
    <PhotoSlot id={`article-inline-${photoKey(src)}`} label={alt || 'Article supporting photo'} className="my-8 w-full" />
  ),
  table: ({ children }) => (
    <div className="dv-editorial__table overflow-x-auto my-8 rounded-lg border border-gray-200 shadow-sm" role="region" aria-label="Article table" tabIndex={0}>
      <table className="min-w-full border-collapse">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="bg-[#0072BB]/10 px-4 py-3 font-semibold text-left text-gray-900 border-b border-gray-200">{children}</th>,
  td: ({ children }) => <td className="px-4 py-3 border-b border-gray-100">{children}</td>,
  pre: ({ children }) => <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-4">{children}</pre>,
  code: ({ children }) => <code className="text-sm font-mono">{children}</code>,
});

const BlogPostPage = () => {
  const { slug } = useParams();
  const [openFaq, setOpenFaq] = useState(null);

  const post = legacyBlogPosts.find((item) => item.slug === slug);
  const related = legacyBlogPosts;

  useEffect(() => {
    setOpenFaq(null);
  }, [slug]);

  const markdownComponents = useMemo(() => makeMarkdownComponents(), []);

  if (!post) {
    return (
      <div className="dv-editorial dv-editorial--not-found" data-testid="blog-not-found">
        <div className="container-custom dv-editorial__not-found-inner">
          <h1 className="dv-size-heading text-4xl font-bold mb-4">Post Not Found</h1>
          <Button asChild><Link to={publicHref("/blog")}>Back to Blog</Link></Button>
        </div>
      </div>
    );
  }

  // Normalize fields
  const title = post.title;
  const excerpt = post.excerpt || '';
  const category = post.category;
  const author = post.author || 'Delta Valley Health Club';
  const date = post.date || '';
  const readTime = post.readTime || post.read_time || '5 min read';
  const body = post.content || post.body_markdown || '';
  const faqs = post.faqs || [];
  const keywords = post.keywords || '';
  // Canonical URL — prefer original WP date URL for SEO equity
  const image = post.image || null; // Preserve article metadata; visible photo slots stay blank.
  const canonicalPath = post.wp_path || `/blog/${slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: excerpt,
    author: { '@type': 'Organization', name: author },
    publisher: {
      '@type': 'Organization',
      name: 'Delta Valley Health Club',
      logo: { '@type': 'ImageObject', url: 'https://deltavalleyhealthclub.com/Delta-Valley-Logo.png' },
    },
    datePublished: date,
    ...(image ? { image } : {}),
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://deltavalleyhealthclub.com${canonicalPath}` },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['[data-speakable="true"]', 'article h1', 'article h2'],
    },
  };

  const faqSchema =
    faqs && faqs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }
      : null;

  const schemas = faqSchema ? [articleSchema, faqSchema] : articleSchema;

  return (
    <div className="dv-editorial dv-editorial--article" data-testid="blog-post-page">
      <SEO
        title={title}
        description={excerpt}
        keywords={keywords}
        schema={schemas}
      />
      <ReadingProgressBar />

      {/* Hero Image */}
      <div
        className="dv-editorial__article-hero"
      >
        <div className="container-custom dv-editorial__article-hero-layout">
          <div className="dv-editorial__article-heading">
            <Badge className="bg-white text-gray-900 mb-5 text-sm py-1 px-3" data-testid="article-category">{category}</Badge>
            <h1 className="dv-size-heading text-4xl md:text-6xl font-bold mb-6 leading-tight tracking-tight" data-testid="article-title">{title}</h1>
            <div className="flex items-center gap-6 text-sm text-gray-200 flex-wrap">
              <div className="flex items-center gap-2">
                <User size={16} />
                {author}
              </div>
              {date && (
                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  {new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Clock size={16} />
                {readTime}
              </div>
            </div>
          </div>
          <PhotoSlot id={`article-${post.slug}`} label={`${post.title} — article photo`} className="dv-editorial__article-image" />
        </div>
      </div>

      {/* Content + Sticky TOC Sidebar */}
      <article className="dv-editorial__article-section">
        <div className="container-custom">
          <div className="dv-editorial__article-layout">

            {/* Left rail: TOC (desktop only) */}
            <aside className="hidden lg:block">
              <TableOfContents key={slug} containerSelector='[data-testid="blog-body-content"]' />
            </aside>

            {/* Main column */}
            <div className="dv-editorial__article-copy">
              <Link to={publicHref("/blog")}>
                <Button variant="ghost" className="mb-6 -ml-4" data-testid="back-to-blog-btn">
                  <ArrowLeft className="mr-2" size={18} />
                  Back to Blog
                </Button>
              </Link>

              {/* Lead paragraph (speakable + drop cap) */}
              {excerpt && (
                <p
                  className="dv-size-lede text-xl md:text-2xl text-gray-700 leading-relaxed mb-10 font-light first-letter:text-6xl first-letter:font-bold first-letter:text-[#0072BB] first-letter:mr-3 first-letter:float-left first-letter:leading-[0.9] first-letter:mt-1"
                  data-speakable="true"
                  data-testid="article-lede"
                >
                  {excerpt}
                </p>
              )}

              <aside className="dv-editorial__disclosure mb-10 p-4 text-sm leading-relaxed text-gray-700">
                <strong>Educational information:</strong> This archived member article is provided for general information and is not medical advice. Consult a qualified healthcare professional before changing an exercise or recovery routine, especially during pregnancy, after an injury, or when managing a health condition. Program availability, prices, and schedules should be confirmed with Delta Valley.
              </aside>

              <div className="dv-size-article prose prose-lg max-w-none" data-testid="blog-body-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {body}
                </ReactMarkdown>
              </div>

              {/* Premium in-article CTA */}
              <ArticleCTABox />

              {/* FAQ Section */}
              {faqs && faqs.length > 0 && (
                <div className="mt-12" data-testid="blog-faq-section">
                  <h2 className="dv-size-heading text-3xl md:text-4xl font-bold mb-6 tracking-tight">Frequently Asked Questions</h2>
                  <div className="space-y-3">
                    {faqs.map((f, i) => (
                      <div key={i} className="dv-editorial__faq">
                        <button
                          aria-expanded={openFaq === i}
                          aria-controls={`article-faq-answer-${i}`}
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                          data-testid={`blog-faq-${i}`}
                        >
                          <span className="font-semibold text-lg pr-4 text-gray-900">{f.q}</span>
                          <ChevronDown size={20} className={`shrink-0 text-[#0072BB] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                        </button>
                        {openFaq === i && (
                          <div id={`article-faq-answer-${i}`} className="px-5 pb-5 text-gray-700 border-t bg-gray-50/50 pt-4 leading-relaxed">{f.a}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Author Box */}
              <div className="dv-editorial__author mt-14" data-testid="article-author-box">
                <div className="flex items-center gap-5">
                  <div className="dv-size-subheading w-16 h-16 md:w-20 md:h-20 shrink-0 rounded-full bg-gradient-to-br from-[#0072BB] to-[#005A94] flex items-center justify-center text-white text-2xl md:text-3xl font-bold shadow-lg">
                    {author.charAt(0)}
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">Written by</div>
                    <h4 className="dv-size-subheading font-bold text-xl text-gray-900">{author}</h4>
                    <p className="text-gray-600 text-sm">Published by Delta Valley Health Club · Brentwood &amp; Discovery Bay, CA</p>
                  </div>
                </div>
              </div>

              {/* Related Articles — premium grid with image previews. Falls back to latest 4 posts if no same-category siblings. */}
              {(() => {
                const sameCat = related.filter((p) => p.slug !== slug && p.category === category);
                const fallback = related.filter((p) => p.slug !== slug);
                const toShow = (sameCat.length > 0 ? sameCat : fallback).slice(0, 4);
                if (toShow.length === 0) return null;
                return (
                  <div className="mt-14" data-testid="related-articles">
                    <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
                      <h3 className="dv-size-subheading text-2xl md:text-3xl font-bold tracking-tight">Continue Reading</h3>
                      <Link to={publicHref("/blog")} className="text-sm text-[#0072BB] hover:text-[#005A94] font-semibold flex items-center gap-1">
                        All articles <ArrowLeft className="rotate-180" size={14} />
                      </Link>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      {toShow.map((relatedPost) => {
                        return (
                          <Link
                            key={relatedPost.slug}
                            to={publicHref(relatedPost.wp_path || `/blog/${relatedPost.slug}`)}
                            className="group"
                            data-testid={`related-card-${relatedPost.slug}`}
                          >
                            <div className="dv-editorial__related-story">
                              <PhotoSlot id={`article-${relatedPost.slug}`} label={`${relatedPost.title} — article photo`} className="h-40" />
                              <div className="p-5">
                                <Badge className="mb-2 bg-[#0072BB]/10 text-[#0072BB] hover:bg-[#0072BB]/15">{relatedPost.category}</Badge>
                                <h4 className="dv-size-subheading font-bold text-lg leading-snug mb-2 text-gray-900 group-hover:text-[#0072BB] transition-colors">{relatedPost.title}</h4>
                                <p className="dv-size-body text-sm text-gray-600 line-clamp-2">{relatedPost.excerpt}</p>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Right rail: spacer (xl+) for visual balance */}
            <aside className="hidden xl:block" aria-hidden="true" />
          </div>
        </div>
      </article>

      <TourClosing />
    </div>
  );
};

export default BlogPostPage;
